import {
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { Card } from '@/components/common';
import { ContactRow } from './portalProfile';

/**
 * M-01 / U-01 — the broker and agent contact blocks, drawn in three places:
 *
 *   * the POSP's "My Broker" page (`MyContactsPage`, broker block only);
 *   * the customer's Settings > Profile, "Your advisors" (both blocks, from
 *     `/customers/me/`);
 *   * the customer's policy detail (`PolicyAdvisorCard`, both blocks, from
 *     that policy's own `advisors`).
 *
 * They were file-local to `MyContactsPage`; lifted here so the three cannot
 * drift. Nothing is stored behind them -- every value is read off the server
 * on each visit -- so moving where they render loses no contact records.
 */

export interface BrokerContact {
  name?: string | null;
  contactPerson?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface AgentContact {
  name?: string | null;
  phone?: string | null;
  email?: string | null;
}

const orUndefined = (value?: string | null) => value || undefined;

export function BrokerContactBlock({ broker }: { broker: BrokerContact }) {
  const empty = !broker.name && !broker.phone && !broker.email;
  return (
    <>
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        <ContactRow icon={<BuildingOfficeIcon className="w-4 h-4" />} label="Company" value={orUndefined(broker.name)} />
        <ContactRow
          icon={<UserIcon className="w-4 h-4" />}
          label="Contact Person"
          value={orUndefined(broker.contactPerson)}
        />
        <ContactRow icon={<PhoneIcon className="w-4 h-4" />} label="Phone" value={orUndefined(broker.phone)} />
        <ContactRow icon={<EnvelopeIcon className="w-4 h-4" />} label="Email" value={orUndefined(broker.email)} />
      </div>
      {empty && <p className="text-sm text-gray-400 dark:text-gray-500 italic">No broker assigned.</p>}
    </>
  );
}

export function AgentContactBlock({ agent }: { agent?: AgentContact | null }) {
  const assigned = !!(agent && (agent.name || agent.phone || agent.email));
  if (!assigned) {
    return (
      <div className="flex items-center gap-2 py-4 text-gray-400 dark:text-gray-500">
        <UserIcon className="w-5 h-5" />
        <span className="text-sm italic">Not assigned</span>
      </div>
    );
  }
  return (
    <div className="divide-y divide-gray-100 dark:divide-gray-700">
      <ContactRow icon={<UserIcon className="w-4 h-4" />} label="Name" value={orUndefined(agent?.name)} />
      <ContactRow icon={<PhoneIcon className="w-4 h-4" />} label="Phone" value={orUndefined(agent?.phone)} />
      <ContactRow icon={<EnvelopeIcon className="w-4 h-4" />} label="Email" value={orUndefined(agent?.email)} />
    </div>
  );
}

function CardTitle({ icon, tone, title }: { icon: React.ReactNode; tone: 'primary' | 'green'; title: string }) {
  const toneClass =
    tone === 'primary'
      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
      : 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400';
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${toneClass}`}>{icon}</div>
      <h2 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h2>
    </div>
  );
}

/**
 * The two side-by-side cards. `showAgent` is false for a POSP, whose own
 * details are not a contact of themselves (they live on /settings > Profile).
 */
export function AdvisorCards({
  broker,
  agent,
  showAgent,
}: {
  broker: BrokerContact;
  agent?: AgentContact | null;
  showAgent: boolean;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card>
        <div className="p-6">
          <CardTitle icon={<BuildingOfficeIcon className="w-6 h-6" />} tone="primary" title="My Broker" />
          <BrokerContactBlock broker={broker} />
        </div>
      </Card>

      {showAgent && (
        <Card>
          <div className="p-6">
            <CardTitle icon={<UserIcon className="w-6 h-6" />} tone="green" title="Policy Agent" />
            <AgentContactBlock agent={agent} />
          </div>
        </Card>
      )}
    </div>
  );
}
